/* Margin Finder · Saúde: o que o `agent_saude` achou, um usuário por vez.
 *
 * Duas coisas separam esta tela das outras três.
 *
 * A primeira é `alertas_do_contrato`: fatos sobre o que JÁ está assinado — reajuste
 * sem teto, rescisão imotivada depois de 12 meses, portabilidade de carências. Não é
 * checklist de compra, é o que muda a decisão de trocar, então tem Card próprio e
 * vem ANTES das propostas.
 *
 * A segunda é `custo_de_encaixe`: quantos crivos a proposta violou para caber. Um
 * plano mais barato com encaixe alto não é o mesmo produto — por isso cada crivo
 * violado sobe como `tag alta`, nunca como nota de rodapé.
 */

import { ChartFrame, Card } from "../../components/ui";
import type { PlanoAtual, SaudePlano, SaudeUser } from "../../lib/api";
import { brl2, fmtMes, intBR } from "../../lib/format";
import { RelatorioEmTexto } from "./Relatorio";
import {
  Barras, BlocoAuditoria, CONFIANCA, Facts, Hero, Passos, PlanoShell, Provenancia,
  SecaoMargin, economiaLabel, lista, money, ou, rotulo,
} from "./comum";

/* ── rótulos ─────────────────────────────────────────────────────────────── */

const PAPEL: Record<string, string> = {
  mais_barato: "mais barato",
  equilibrado: "equilibrado",
  melhor_rede: "melhor rede",
};

const ACOMODACAO: Record<string, string> = {
  enfermaria: "enfermaria",
  apartamento: "apartamento",
};

const REDE: Record<string, string> = {
  intermediaria: "rede intermediária",
  premium: "rede premium",
  basica: "rede básica",
};

const COPARTICIPACAO: Record<string, string> = {
  nao: "sem coparticipação",
  parcial: "coparticipação parcial",
  sim: "com coparticipação",
};

const CONTRATACAO: Record<string, string> = {
  coletivo_empresarial: "coletivo empresarial",
  coletivo_por_adesao: "coletivo por adesão",
  individual: "individual",
};

const SEGMENTACAO: Record<string, string> = {
  ambulatorial_hospitalar_obstetricia: "ambulatorial + hospitalar + obstetrícia",
  ambulatorial_hospitalar: "ambulatorial + hospitalar",
  ambulatorial: "ambulatorial",
};

const PRECO_ORIGEM: Record<string, string> = {
  curva_propria: "curva própria",
  curva_mercado: "curva de mercado",
  tabela: "tabela publicada",
};

const CRIVO: Record<string, string> = {
  padrao_rede: "padrão de rede",
  acomodacao: "acomodação",
  coparticipacao: "coparticipação",
  abrangencia: "abrangência",
  reembolso: "reembolso",
};

const TIPO_PLANO: Record<string, string> = {
  odonto: "odontológico",
  saude: "saúde",
};

const STATUS: Record<string, string> = {
  trocar: "vale trocar",
  manter: "vale manter",
  dados_insuficientes: "dados insuficientes",
};

/* ── o cartão de plano ───────────────────────────────────────────────────── */

function SaudePlanoCard({ plano, escolhido }: { plano: SaudePlano; escolhido: boolean }) {
  const dependentes = plano.vidas_cobertas === null ? null : plano.vidas_cobertas - 1;
  return (
    <PlanoShell
      papel={rotulo(PAPEL, plano.papel)}
      escolhido={escolhido}
      titulo={plano.plano}
      sub={plano.operadora}
      preco={money(plano.custo_familia_mensal_brl)}
      precoNota={plano.vidas_cobertas === null
        ? "/mês" : `/mês · ${intBR(plano.vidas_cobertas)} vidas`}
      porQue={plano.por_que}
      atencao={plano.atencao}
      fonteUrl={plano.fonte_url}
      chips={
        <>
          <span className="chip">{rotulo(ACOMODACAO, plano.acomodacao)}</span>
          <span className="chip">{rotulo(REDE, plano.padrao_rede)}</span>
          <span className="chip">{rotulo(COPARTICIPACAO, plano.coparticipacao)}</span>
          <span className="chip">{plano.abrangencia}</span>
          <span className="chip">{rotulo(CONTRATACAO, plano.tipo_contratacao)}</span>
          {plano.reembolso === "sim" && <span className="chip">com reembolso</span>}
          {plano.preco_estimado && (
            <span className="tag media">
              preço estimado · {rotulo(PRECO_ORIGEM, plano.preco_origem)}
            </span>
          )}
          {plano.confianca_do_encaixe === "baixa" && (
            <span className="tag alta">{rotulo(CONFIANCA, plano.confianca_do_encaixe)}</span>
          )}
          {/* Cada crivo violado é um jeito de o plano NÃO ser o produto pedido. */}
          {lista(plano.crivos_violados).map((c) => (
            <span className="tag alta" key={c}>viola {rotulo(CRIVO, c)}</span>
          ))}
        </>
      }
    >
      <div className="mf-plan-linha">
        titular {money(plano.preco_titular_mensal_brl)}
        {dependentes !== null && dependentes > 0 && (
          <> · {intBR(dependentes)} dependente(s) a {money(plano.preco_dependente_mensal_brl)}</>
        )}
      </div>
      {/* IDSS e IGR são índices da ANS: sem o rótulo não querem dizer nada. */}
      <div className="mf-plan-cobertura">
        IDSS {plano.idss ?? "—"} · IGR {plano.igr ?? "—"}
        {plano.custo_de_encaixe !== null && <> · encaixe {plano.custo_de_encaixe}</>}
      </div>
      <p className="mf-nota">{rotulo(SEGMENTACAO, plano.segmentacao)}</p>
    </PlanoShell>
  );
}

function PlanoAtualCard({ p }: { p: PlanoAtual }) {
  const duvidoso = p.tipo_provavel != null && p.tipo_provavel !== p.tipo;
  return (
    <article className="mf-assin" data-status={p.tipo}>
      <div className="mf-assin-head">
        <div className="mf-assin-id">
          <h3>{ou(p.operadora)}</h3>
          <div className="mf-assin-meta">
            {rotulo(TIPO_PLANO, p.tipo)}
            {p.ultimo_mes && <> · último mês {fmtMes(p.ultimo_mes)}</>}
          </div>
        </div>
        <div className="mf-assin-num">
          <div className="v">{money(p.custo_mensal_estimado_brl)}<small>/mês</small></div>
        </div>
      </div>
      <div className="chips">
        <span className="chip">{rotulo(CONFIANCA, p.confianca_identificacao)}</span>
        {/* O erro mais caro desta tela: cobrança classificada como odonto que na
            verdade é plano médico. O chip existe para ela não passar calada. */}
        {duvidoso && (
          <span className="tag alta">provavelmente {rotulo(TIPO_PLANO, p.tipo_provavel!)}</span>
        )}
        {p.alerta_qualidade && <span className="tag alta">{p.alerta_qualidade}</span>}
      </div>
      {p.observacao && <p className="mf-nota">{p.observacao}</p>}
    </article>
  );
}

/* ── a sessão ────────────────────────────────────────────────────────────── */

export function Saude() {
  return (
    <SecaoMargin<SaudeUser>
      chave="saude"
      vazio="Usuário sem análise de plano de saúde."
      optionLabel={economiaLabel}
    >
      {(user) => <SaudeDetalhe user={user} />}
    </SecaoMargin>
  );
}

function SaudeDetalhe({ user }: { user: SaudeUser }) {
  const a = user.analise;
  const auditoria = a.auditoria ?? {} as typeof a.auditoria;
  const propostas = lista(a.propostas);
  const atuais = lista(a.planos_atuais);
  const alertas = lista(a.alertas_do_contrato);

  const barras = [
    ...(a.valor_atual_brl !== null
      ? [{ key: "hoje", rotulo: "hoje", valor: a.valor_atual_brl, destaque: true }]
      : []),
    ...propostas
      .filter((p) => p.custo_familia_mensal_brl !== null)
      .map((p) => ({
        key: p.id_plano,
        rotulo: p.operadora,
        valor: p.custo_familia_mensal_brl as number,
        qtd: p.id_catalogo === a.melhor_plano_id_catalogo ? "escolhido" : "",
      })),
  ];

  return (
    <div className="mf-page">
      {/* 1 · a decisão */}
      <Card>
        <Hero
          estado={user.estado_economia}
          titulo="economia estimada"
          valor={user.economia_anual_brl === null
            ? "sem base comparável"
            : `R$ ${brl2(user.economia_anual_brl)}`}
          detalhe={user.economia_anual_brl === null
            ? "O agente não achou lançamento suficiente para comparar."
            : <>por ano · {money(user.economia_mensal_brl)}/mês</>}
          faixa
          lado={user.custo_novo_brl === null ? undefined : {
            k: "plano indicado",
            v: `${money(user.custo_novo_brl)}/mês`,
            d: <>
              {ou(user.plano_escolhido_operadora)} · {ou(user.plano_escolhido_nome)}
              {user.vidas_cobertas !== null && <> · {intBR(user.vidas_cobertas)} vidas</>}
            </>,
          }}
        />
        <p className="mf-frase">
          {a.status !== "trocar" && (
            <span className="tag" style={{ marginRight: 8 }}>{rotulo(STATUS, a.status)}</span>
          )}
          {a.resumo}
        </p>
      </Card>

      {/* 2 · a evidência: o plano de hoje */}
      <Card title="Plano atual" sub="o que o agente encontrou nos lançamentos">
        {a.base_comparavel === false && (
          <div className="banner" role="status">
            <span aria-hidden>⚠</span>
            <span>
              Sem base comparável: os lançamentos não bastam para dizer quanto sai por
              mês em plano de saúde.
            </span>
          </div>
        )}
        <Facts items={[
          { k: "operadora de hoje", v: ou(a.operadora_atual, "não identificada") },
          { k: "sai por mês", v: money(a.valor_atual_brl) },
          { k: "praça", v: ou(auditoria.praca) },
          { k: "faixa etária usada", v: ou(auditoria.faixa_etaria_usada) },
        ]} />
        {atuais.length > 0 && (
          <div className="mf-assins" style={{ marginTop: 14 }}>
            {atuais.map((p, i) => <PlanoAtualCard key={i} p={p} />)}
          </div>
        )}
      </Card>

      {/* 3 · o que já está assinado — antes de qualquer proposta */}
      {alertas.length > 0 && (
        <Card title="Alertas do contrato"
              sub="regras do produto que mudam a decisão, não passos de compra">
          <div className="banner" role="status">
            <span aria-hidden>⚠</span>
            <span>Leia antes de comparar preço.</span>
          </div>
          <ul className="mf-why">
            {alertas.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </Card>
      )}

      {/* 4 · a ação: para onde ir */}
      {propostas.length > 0 && (
        <ChartFrame
          title="Para onde ir"
          sub="IDSS e IGR são índices da ANS: IDSS maior é melhor, IGR menor é melhor"
          tableHeader={["papel", "operadora", "plano", "R$/mês família", "acomodação", "rede", "IDSS", "IGR", "encaixe"]}
          tableRows={propostas.map((p) => [
            rotulo(PAPEL, p.papel), p.operadora, p.plano,
            p.custo_familia_mensal_brl ?? "—", rotulo(ACOMODACAO, p.acomodacao),
            rotulo(REDE, p.padrao_rede), p.idss ?? "—", p.igr ?? "—",
            p.custo_de_encaixe ?? "—",
          ])}
        >
          <div className="mf-plans">
            {propostas.map((p) => (
              <SaudePlanoCard key={p.id_plano} plano={p}
                              escolhido={p.id_catalogo === a.melhor_plano_id_catalogo} />
            ))}
          </div>
        </ChartFrame>
      )}

      {/* 5 · a ação: a lista antes de assinar */}
      <Passos title="Antes de contratar" items={lista(a.antes_de_contratar)} />

      {/* 6 · o método */}
      {barras.length > 1 && (
        <ChartFrame
          title="O custo de cada saída"
          sub="custo familiar mensal por proposta, contra o que sai hoje"
          tableHeader={["opção", "R$/mês"]}
          tableRows={barras.map((b) => [b.rotulo, b.valor])}
        >
          <Barras rows={barras} />
        </ChartFrame>
      )}

      {/* 7 · as ressalvas */}
      <Card title="Premissas e ressalvas" sub="o que o agente assumiu para chegar aqui">
        {lista(a.premissas).length > 0 && (
          <ul className="mf-why">
            {lista(a.premissas).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        )}
        <BlocoAuditoria
          limitacoes={lista(auditoria.limitacoes)}
          blocosRecebidos={lista(auditoria.blocos_recebidos)}
          blocosAusentes={lista(auditoria.blocos_ausentes)}
          idsCitados={lista(auditoria.ids_citados)}
          regrasVioladas={lista(auditoria.regras_de_piso_violadas)}
        >
          <Facts items={[
            { k: "planos no catálogo", v: auditoria.planos_no_catalogo?.toString() ?? "—" },
            { k: "planos na praça", v: auditoria.planos_na_praca?.toString() ?? "—" },
            { k: "planos elegíveis", v: auditoria.planos_elegiveis?.toString() ?? "—" },
          ]} />
          {lista(auditoria.filtros_aplicados).length > 0 && (
            <ul className="mf-why">
              {lista(auditoria.filtros_aplicados).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          )}
          {lista(auditoria.crivos_relaxados).length > 0 && (
            <p className="mf-nota">
              Crivos relaxados para chegar a três candidatos:{" "}
              {lista(auditoria.crivos_relaxados).join(", ")}
            </p>
          )}
        </BlocoAuditoria>
      </Card>

      {/* 8 · o texto do agente */}
      <RelatorioEmTexto userId={user.user_id} agentId="agent_saude" />

      {/* 9 · a procedência */}
      <Provenancia user={user} agentId="agent_saude" />
    </div>
  );
}
